import React, { useEffect, useState } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvents,
  LayersControl,
  GeoJSON,
  Pane,
  CircleMarker
} from 'react-leaflet';
import L from 'leaflet';
import { useLanguage } from '../context/LanguageContext';
import { useConfig } from '../context/ConfigContext';
import { wetlandService } from '../services/apiService';
import ExpandingFab from './ExpandingFab';
import SearchFilter from './SearchFilter';
import WetlandDetails from './WetlandDetails';
import './Map.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const CLUSTER_DISTANCE_PIXELS = 56;
const CLUSTER_DISABLE_ZOOM = 14;
const CLUSTER_EXPAND_ZOOM = 16;

const getWetlandPosition = (wetland) => {
  const latitude = Number(wetland.location?.latitude);
  const longitude = Number(wetland.location?.longitude);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return [latitude, longitude];
};

const createClusterIcon = (count) => L.divIcon({
  html: `<div class="wetland-cluster-marker">${count}</div>`,
  className: 'wetland-cluster-icon',
  iconSize: [44, 44],
  iconAnchor: [22, 22],
});

const WetlandMarker = ({ wetland, position, onViewDetails, t }) => (
  <Marker
    key={wetland.id}
    position={position}
  >
    <Popup className="wetland-popup">
      <div>
        <h3>{t(wetland.wetlandType)}</h3>
        <p><strong>{t('waterClarity')}:</strong> {t(wetland.waterClarity)}</p>
        <p><strong>{t('waterColor')}:</strong> {t(wetland.waterColor)}</p>
        {wetland.observedThreatsList && wetland.observedThreatsList.length > 0 && (
          <p><strong>{t('observedThreats')}:</strong> {wetland.observedThreatsList.length}</p>
        )}
        <div className="popup-actions">
          <button
            className="view-details"
            onClick={() => onViewDetails(wetland)}
          >
            {t('viewDetails') || 'Detalji'}
          </button>
        </div>
      </div>
    </Popup>
  </Marker>
);

const ClusteredWetlandMarkers = ({ wetlands, mapZoom, onViewDetails, t }) => {
  const map = useMap();

  const validWetlands = wetlands
    .map((wetland) => ({
      wetland,
      position: getWetlandPosition(wetland)
    }))
    .filter(({ position }) => position);

  const clusters = validWetlands.reduce((acc, item) => {
    if (mapZoom >= CLUSTER_DISABLE_ZOOM) {
      acc.push({
        wetlands: [item.wetland],
        positions: [item.position],
        position: item.position,
        point: map.latLngToLayerPoint(item.position)
      });
      return acc;
    }

    const point = map.latLngToLayerPoint(item.position);
    let closestCluster = null;
    let closestDistance = Infinity;

    acc.forEach((cluster) => {
      const distance = point.distanceTo(cluster.point);
      if (distance < CLUSTER_DISTANCE_PIXELS && distance < closestDistance) {
        closestCluster = cluster;
        closestDistance = distance;
      }
    });

    if (!closestCluster) {
      acc.push({
        wetlands: [item.wetland],
        positions: [item.position],
        position: item.position,
        point
      });
      return acc;
    }

    const previousCount = closestCluster.wetlands.length;
    closestCluster.wetlands.push(item.wetland);
    closestCluster.positions.push(item.position);
    closestCluster.point = L.point(
      ((closestCluster.point.x * previousCount) + point.x) / (previousCount + 1),
      ((closestCluster.point.y * previousCount) + point.y) / (previousCount + 1)
    );
    closestCluster.position = map.layerPointToLatLng(closestCluster.point);

    return acc;
  }, []);

  const expandCluster = (cluster) => {
    const bounds = L.latLngBounds(cluster.positions);
    const southWest = bounds.getSouthWest();
    const northEast = bounds.getNorthEast();

    if (southWest.equals(northEast)) {
      map.setView(cluster.position, Math.max(map.getZoom() + 2, CLUSTER_DISABLE_ZOOM));
      return;
    }

    map.fitBounds(bounds, {
      padding: [60, 60],
      maxZoom: CLUSTER_EXPAND_ZOOM
    });
  };

  return (
    <>
      {clusters.map((cluster, index) => {
        if (cluster.wetlands.length === 1) {
          const wetland = cluster.wetlands[0];
          return (
            <WetlandMarker
              key={wetland.id}
              wetland={wetland}
              position={cluster.positions[0]}
              onViewDetails={onViewDetails}
              t={t}
            />
          );
        }

        return (
          <Marker
            key={`cluster-${index}-${cluster.wetlands.map((wetland) => wetland.id).join('-')}`}
            position={cluster.position}
            icon={createClusterIcon(cluster.wetlands.length)}
            eventHandlers={{
              click: () => expandCluster(cluster)
            }}
          />
        );
      })}
    </>
  );
};

// Show FAB only on real mobile devices
// navigator.platform does NOT change in browser DevTools mobile simulation
const isRealMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const platform = (navigator.platform || '').toLowerCase();
  const isDesktop = /win|mac|linux x86/.test(platform);
  return !isDesktop;
};

const Map = ({ onAddNewWetland }) => {
  const { t } = useLanguage();
  const { showWetlandPins, showAddWetlandFab } = useConfig();
  const [wetlands, setWetlands] = useState([]);
  const [filteredWetlands, setFilteredWetlands] = useState([]);
  const [loading, setLoading] = useState(showWetlandPins);
  const [isAdding, setIsAdding] = useState(false);
  const [newMarkerPosition, setNewMarkerPosition] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchFilters, setSearchFilters] = useState(null);
  const [selectedWetland, setSelectedWetland] = useState(null);
  const [serbiaBoundary, setSerbiaBoundary] = useState(null);
  const [userLocation, setUserLocation] = useState(null);

  const serbiaCenter = [44.0165, 21.0059];
  const defaultZoom = 7;

  const [mapCenter, setMapCenter] = useState(serbiaCenter);
  const [mapZoom, setMapZoom] = useState(defaultZoom);

  // real mobile device detection (not affected by browser resize or devtools)
  const isMobile = isRealMobileDevice();

  useEffect(() => {
    if (showWetlandPins) loadWetlands();
  }, [showWetlandPins]);

  // Get user's current location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation([position.coords.latitude, position.coords.longitude]);
        },
        (error) => {
          console.error('Geolocation error:', error);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, []);

  useEffect(() => {
    if (!searchFilters) {
      setFilteredWetlands(wetlands);
      return;
    }

    const filtered = wetlands.filter((wetland) => {
      if (searchFilters.dateFrom || searchFilters.dateTo) {
        const wetlandDate = new Date(wetland.observationTimestamp);
        if (searchFilters.dateFrom && wetlandDate < new Date(searchFilters.dateFrom)) {
          return false;
        }
        if (searchFilters.dateTo && wetlandDate > new Date(searchFilters.dateTo)) {
          return false;
        }
      }

      if (searchFilters.wetlandType && wetland.wetlandType !== searchFilters.wetlandType) {
        return false;
      }

      if (searchFilters.municipality && wetland.municipality) {
        if (!wetland.municipality.toLowerCase().includes(searchFilters.municipality.toLowerCase())) {
          return false;
        }
      }

      if (searchFilters.cadastralMunicipality && wetland.cadastralMunicipality) {
        if (!wetland.cadastralMunicipality.toLowerCase().includes(searchFilters.cadastralMunicipality.toLowerCase())) {
          return false;
        }
      }

      return true;
    });

    setFilteredWetlands(filtered);
  }, [searchFilters, wetlands]);

  const loadWetlands = async () => {
    try {
      setLoading(true);
      const data = await wetlandService.getWetlands();
      const wetlandsArray = Array.isArray(data) ? data : [];
      setWetlands(wetlandsArray);
      setFilteredWetlands(wetlandsArray);
    } catch (error) {
      console.error('Error loading wetlands:', error);
      setWetlands([]);
      setFilteredWetlands([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
    setNewMarkerPosition(null);
  };

  const handleConfirmAdd = () => {
    if (newMarkerPosition) {
      onAddNewWetland(newMarkerPosition);
    }
    setIsAdding(false);
    setNewMarkerPosition(null);
  };

  const handleSearch = (filters) => {
    setSearchFilters(filters);
  };

  const handleResetSearch = () => {
    setSearchFilters(null);
    setFilteredWetlands(wetlands);
  };

  const MapEventsHandler = () => {
    const map = useMapEvents({
      click(e) {
        if (isAdding) {
          setNewMarkerPosition(e.latlng);
        }
      },
      zoomend() {
        setMapZoom(map.getZoom());
      },
      moveend() {
        setMapCenter(map.getCenter());
      },
    });
    return null;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(
          'https://nominatim.openstreetmap.org/search.php?q=Serbia&polygon_geojson=1&format=jsonv2',
          {
            headers: {
              'Accept': 'application/json',
              'User-Agent': 'wetlands.rs map (educational, non-commercial)'
            }
          }
        );
        const json = await resp.json();
        const feature = json.find(
          (r) => r && r.geojson && (r.geojson.type === 'Polygon' || r.geojson.type === 'MultiPolygon')
        );
        if (!cancelled && feature) {
          setSerbiaBoundary({
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: { name: 'Serbia' },
                geometry: feature.geojson
              }
            ]
          });
        }
      } catch (e) {
        console.error('Failed to load Serbia boundary:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className={`map-container ${isAdding ? 'adding-mode' : ''}`}>
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ width: '100%', height: '100%' }}
        zoomControl={true}
      >
        <MapEventsHandler />

        <LayersControl position="topright" style={{ marginTop: '60px' }}>
          <LayersControl.BaseLayer checked name="OpenStreetMap">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Google Satellite">
            <TileLayer
              attribution='&copy; Google'
              url="http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}"
              maxZoom={20}
              subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="CARTO Positron">
            <TileLayer
              attribution='&copy; <a href="https://carto.com/attributions">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              maxZoom={20}
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        {serbiaBoundary && (
          <Pane name="serbia-outline" style={{ zIndex: 450 }}>
            <GeoJSON
              data={serbiaBoundary}
              style={{
                color: '#0818ffff',
                weight: 3,
                opacity: 0.3,
                dashArray: '4 2',
                fill: false
              }}
              interactive={false}
            />
          </Pane>
        )}

        {showWetlandPins && (
          <ClusteredWetlandMarkers
            wetlands={filteredWetlands}
            mapZoom={mapZoom}
            onViewDetails={setSelectedWetland}
            t={t}
          />
        )}

        {newMarkerPosition && <Marker position={newMarkerPosition} />}

        {userLocation && (
          <CircleMarker
            center={userLocation}
            radius={8}
            pathOptions={{
              color: '#ffffff',
              fillColor: '#4285f4',
              fillOpacity: 1,
              weight: 3
            }}
          >
            <Popup>{t('youAreHere')}</Popup>
          </CircleMarker>
        )}
      </MapContainer>

      {showWetlandPins && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 500
        }}>
          <button
            onClick={() => setShowSearch(true)}
            style={{
              background: 'white',
              border: '2px solid rgba(0,0,0,0.2)',
              borderRadius: '5px',
              width: '45px',
              height: '45px',
              fontSize: '1.5rem',
              cursor: 'pointer',
              boxShadow: '0px 1px rgba(0,0,0,0.65)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s',
              padding: 0
            }}
            onMouseEnter={(e) => e.target.style.background = '#f4f4f4'}
            onMouseLeave={(e) => e.target.style.background = 'white'}
            title={t('search')}
          >
            🔍
          </button>
        </div>
      )}

      {loading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          padding: '1rem 2rem',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          zIndex: 1000
        }}>
          <div className="spinner"></div>
        </div>
      )}

      {showWetlandPins && searchFilters && (
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'white',
          padding: '0.75rem 1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <span>{t('searchResults')}: {filteredWetlands.length}</span>
          <button
            onClick={handleResetSearch}
            style={{
              background: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            {t('reset')}
          </button>
        </div>
      )}

      {isMobile && showAddWetlandFab && (
        <ExpandingFab
          isExpanded={isAdding}
          onFabClick={() => setIsAdding(true)}
          onConfirmClick={handleConfirmAdd}
          onCancelClick={handleCancelAdd}
        />
      )}

      {showWetlandPins && showSearch && (
        <SearchFilter
          onSearch={handleSearch}
          onClose={() => setShowSearch(false)}
        />
      )}

      {selectedWetland && (
        <WetlandDetails
          wetland={selectedWetland}
          onClose={() => setSelectedWetland(null)}
        />
      )}
    </div>
  );
};

export default Map;
